import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Grid,
  Button,
  ButtonGroup,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
  Stack,
  LinearProgress,
  IconButton,
  Tooltip,
  Fade,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  TrendingUp,
  AccountBalance,
  PendingActions,
  People,
  Refresh,
  Timeline,
  AttachMoney,
  Warning,
} from "@mui/icons-material";
import { AgentService } from "../services/agent.service";
import { ClientStatistics } from "../models/statistics.models";
import { useSelector } from "react-redux";
import { StoreState } from "../store/store";

interface StatisticCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  trend?: {
    value: number;
    isPositive: boolean;
  };
  progress?: number;
}

const StatisticCard: React.FC<StatisticCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  trend,
  progress,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Card
      elevation={3}
      sx={{
        height: '100%',
        minHeight: isMobile ? '220px' : '260px',
        transition: 'all 0.3s ease-in-out',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[6],
        },
        background: `linear-gradient(135deg, ${theme.palette[color].main}08, ${theme.palette.background.paper})`,
        border: `1px solid ${theme.palette[color].light}20`,
      }}
    >
      <CardContent 
        sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', minHeight: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                color="text.secondary" 
                sx={{ 
                  fontWeight: 500, 
                  mb: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant={isMobile ? "h4" : "h3"} 
                sx={{ 
                  fontWeight: 700, 
                  color: theme.palette[color].main,
                  fontSize: { xs: '1.3rem', sm: '1.8rem', md: '2.2rem' },
                  lineHeight: 1.1,
                  wordBreak: 'break-word',
                  hyphens: 'auto',
                }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 0.5,
                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                p: { xs: 1, sm: 1.5 },
                borderRadius: 2,
                backgroundColor: `${theme.palette[color].main}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {React.cloneElement(icon as React.ReactElement, {
                sx: { 
                  fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' }, 
                  color: theme.palette[color].main 
                }
              })}
            </Box>
          </Box>

          {progress !== undefined && (
            <Box sx={{ minHeight: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                >
                  Collection Progress
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.7rem', sm: '0.75rem' }
                  }}
                >
                  {progress.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: { xs: 4, sm: 6 },
                  borderRadius: 3,
                  backgroundColor: theme.palette.grey[200],
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    background: `linear-gradient(45deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
                  },
                }}
              />
            </Box>
          )}

          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 0, mt: 'auto' }}>
              <Chip
                label={`${trend.isPositive ? '+' : ''}${trend.value.toFixed(1)}%`}
                color={trend.isPositive ? 'success' : 'error'}
                size="small"
                icon={<TrendingUp />}
                sx={{ 
                  fontWeight: 600,
                  height: { xs: 24, sm: 28 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  '& .MuiChip-icon': {
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }
                }}
              />
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.6rem', sm: '0.7rem' },
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                vs previous period
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const LoadingSkeleton: React.FC = () => (
  <Grid container spacing={3}>
    {[1, 2, 3, 4].map((item) => (
      <Grid item xs={12} sm={6} md={3} key={item}>
        <Card elevation={3}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={48} sx={{ mt: 1 }} />
                </Box>
                <Skeleton variant="circular" width={56} height={56} />
              </Box>
              <Skeleton variant="rectangular" height={6} sx={{ borderRadius: 3 }} />
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

export const AgentHomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector((state: StoreState) => state.user.loggedInUser);

  const [clientStatistics, setClientStatistics] = useState<ClientStatistics>({
    totalAmount: 0,
    leftToPay: 0,
    unpayedClientsAmount: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number | undefined>(7);
  const [refreshing, setRefreshing] = useState(false);

  const periodOptions = [
    { label: '7 Days', value: 7, color: 'secondary' as const },
    { label: '30 Days', value: 30, color: 'info' as const },
    { label: '1 Year', value: 365, color: 'success' as const },
    { label: 'All Time', value: undefined, color: 'primary' as const },
  ];

  useEffect(() => {
    fetchClientStatistics(7); // Default to 7 days
  }, []);

  const fetchClientStatistics = async (periodInDays?: number) => {
    try {
      setLoading(true);
      setError(null);
      const statistics = await AgentService.getClientsDealsStatistics(periodInDays);
      setClientStatistics(statistics);
      setSelectedPeriod(periodInDays);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchClientStatistics(selectedPeriod);
    setRefreshing(false);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const paidAmount = clientStatistics.totalAmount - clientStatistics.leftToPay;
  const collectionRate = clientStatistics.totalAmount > 0 
    ? (paidAmount / clientStatistics.totalAmount) * 100 
    : 0;

  const getActivePeriodLabel = () => {
    const activePeriod = periodOptions.find(p => p.value === selectedPeriod);
    return activePeriod?.label || '7 Days';
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={() => fetchClientStatistics()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.grey[50] }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 800,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Agent Dashboard
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                Welcome back, {user?.email || 'Agent'}
              </Typography>
            </Box>
            <Tooltip title="Refresh Data">
              <IconButton 
                onClick={handleRefresh} 
                disabled={refreshing}
                sx={{ 
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                <Refresh sx={{ 
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              alignItems="center" 
              justifyContent="space-between"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Timeline color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Period: {getActivePeriodLabel()}
                </Typography>
                {selectedPeriod && (
                  <Chip 
                    label="Active Filter" 
                    color="primary" 
                    size="small" 
                    variant="outlined" 
                  />
                )}
              </Box>
              
              <ButtonGroup 
                variant="outlined" 
                size={isMobile ? "small" : "medium"}
                sx={{ flexWrap: 'wrap' }}
              >
                {periodOptions.map((period) => (
                  <Button
                    key={period.label}
                    onClick={() => fetchClientStatistics(period.value)}
                    variant={selectedPeriod === period.value ? 'contained' : 'outlined'}
                    color={period.color}
                    disabled={loading}
                  >
                    {period.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Stack>
          </Paper>
        </Box>

        {/* Statistics Cards */}
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <Fade in={!loading}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatisticCard
                  title="Total Revenue"
                  value={formatCurrency(clientStatistics.totalAmount)}
                  subtitle="Gross revenue generated"
                  icon={<AccountBalance />}
                  color="primary"
                  // trend={{ value: 12.5, isPositive: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatisticCard
                  title="Amount Collected"
                  value={formatCurrency(paidAmount)}
                  subtitle="Successfully collected"
                  icon={<AttachMoney />}
                  color="success"
                  progress={collectionRate}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatisticCard
                  title="Outstanding Amount"
                  value={formatCurrency(clientStatistics.leftToPay)}
                  subtitle="Pending collection"
                  icon={<PendingActions />}
                  color="warning"
                  // trend={{ value: -8.2, isPositive: false }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatisticCard
                  title="Unpaid Clients"
                  value={clientStatistics.unpayedClientsAmount.toString()}
                  subtitle="Require follow-up"
                  icon={<Warning />}
                  color="error"
                />
              </Grid>
            </Grid>
          </Fade>
        )}

        {/* Summary Section */}
        <Fade in={!loading}>
          <Box sx={{ mt: 4 }}>
            <Card 
              elevation={3} 
              sx={{ 
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography 
                  variant={isMobile ? "h6" : "h5"} 
                  sx={{ 
                    fontWeight: 600, 
                    mb: { xs: 2, sm: 3 }, 
                    textAlign: 'center',
                    fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                  }}
                >
                  Performance Summary
                </Typography>
                
                <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 0 } }}>
                      <Typography 
                        variant={isMobile ? "h3" : "h2"} 
                        sx={{ 
                          fontWeight: 700, 
                          color: 'success.main',
                          fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.8rem' },
                          lineHeight: 1.1,
                          wordBreak: 'break-word',
                          hyphens: 'auto',
                        }}
                      >
                        {collectionRate.toFixed(1)}%
                      </Typography>
                      <Typography 
                        variant={isMobile ? "body1" : "h6"} 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' } }}
                      >
                        Collection Rate
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={8}>
                    <Stack spacing={{ xs: 1.5, sm: 2 }}>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 600,
                              fontSize: { xs: '0.85rem', sm: '0.9rem', md: '1rem' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                            }}
                          >
                            Revenue Collection Progress
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
                              minWidth: 'fit-content',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatCurrency(paidAmount)} of {formatCurrency(clientStatistics.totalAmount)}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={collectionRate}
                          sx={{
                            height: { xs: 8, sm: 10, md: 12 },
                            borderRadius: 6,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                            },
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        pt: { xs: 1, sm: 2 },
                        gap: 1,
                        flexWrap: isMobile ? 'wrap' : 'nowrap'
                      }}>
                        <Chip 
                          label={`${formatCurrency(paidAmount)} Collected`}
                          color="success"
                          icon={<AttachMoney />}
                          size={isMobile ? "small" : "medium"}
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            '& .MuiChip-icon': {
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            },
                            '& .MuiChip-label': {
                              whiteSpace: 'nowrap',
                            },
                            flexShrink: 0,
                          }}
                        />
                        <Chip 
                          label={`${clientStatistics.unpayedClientsAmount} Clients Pending`}
                          color="warning"
                          icon={<People />}
                          size={isMobile ? "small" : "medium"}
                          sx={{ 
                            fontSize: { xs: '0.65rem', sm: '0.7rem' },
                            '& .MuiChip-icon': {
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            },
                            '& .MuiChip-label': {
                              whiteSpace: 'nowrap',
                            },
                            flexShrink: 0,
                          }}
                        />
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};